import { type AutomationEntitySet, type QueryableEntitySet } from "../api/queryableEntitySet.ts";
import {
    type ODataActivityLog,
    type ODataArchivedSiteCollection,
    type ODataAutomation,
    type ODataBrokenInheritanceEntity,
    type ODataCustomReport,
    type ODataDoNotDisturbUser,
    type ODataDoNotDisturbWorkspace,
    type ODataDocumentLibrary,
    type ODataGroup,
    type ODataGroupPrincipal,
    type ODataGroupVisibility,
    type ODataLicense,
    type ODataOneDrive,
    type ODataReportShare,
    type ODataResourceType,
    type ODataRestrictedAccessControl,
    type ODataRestrictedContentDiscovery,
    type ODataReview,
    type ODataReviewAnswerFile,
    type ODataReviewAnswerMembership,
    type ODataReviewAnswerSharedLink,
    type ODataReviewAnswerSharedLinkRecipient,
    type ODataReviewItem,
    type ODataReviewNotificationRecipient,
    type ODataReviewReviewer,
    type ODataReviewSelectedWorkspace,
    type ODataSecurityGroup,
    type ODataSensitivityLabel,
    type ODataSharedLink,
    type ODataSharedLinkInvitation,
    type ODataSharingCapabilities,
    type ODataSiteCollection,
    type ODataUser,
    type ODataUserLicense,
    type ODataWorkspace,
    type TenantCrawlOverviewDto,
    type TenantSharingCapabilities
} from "../api/types.ts";
import { formatEntityDisplayName } from "../api/formatEntityDisplayName.ts";

export function getMainSiteCollectionType(siteCollectionTypes: ODataResourceType[]): ODataResourceType {
    if (siteCollectionTypes.includes("teams")) {
        return "teams";
    }

    if (siteCollectionTypes.includes("oneDrive")) {
        return "oneDrive";
    }

    if (siteCollectionTypes.includes("teamsChannel")) {
        return "teamsChannel";
    }

    // after all the others
    if (siteCollectionTypes.includes("group")) {
        return "group";
    }

    // the last fallback
    if (siteCollectionTypes.includes("sharePointSite")) {
        return "sharePointSite";
    }

    return "unknown";
}

export function isOneDriveSiteCollection(siteCollectionTypes: ODataResourceType[]) {
    return siteCollectionTypes.includes("oneDrive");
}

export function isGroupSiteCollection(siteCollectionTypes: ODataResourceType[]) {
    return siteCollectionTypes.includes("group") || siteCollectionTypes.includes("teams");
}

// The Sharing links tab and its count badge must agree on what counts as "this workspace's
// links", so both build their filter from this one function.
export function buildSiteCollectionSharingLinksFilter(siteCollectionId: string) {
    return `siteCollection/id eq ${siteCollectionId}`;
}

export function getGroupVisibilityToText(visibility: ODataGroupVisibility | null | undefined) {
    switch (visibility) {
        case "private":
            return "Private";
        case "public":
            return "Public";
        case "hiddenMembership":
            return "Hidden Membership";
        default:
            return "Unknown";
    }
}

export function getSharingCapabilityToText(capability: ODataSharingCapabilities | null | undefined) {
    switch (capability) {
        case "noExternalSharing":
            return "People in your organization";
        case "existingGuestsOnly":
            return "Existing guests only";
        case "newAndExistingGuests":
            return "New and existing guests";
        case "anyone":
            return "Anyone";
        default:
            return "Unknown";
    }
}

export function formatEntityCount(count: number, entity: AutomationEntitySet) {
    const name = formatEntityDisplayName(entity, {
        capitalization: "lowercase",
        form: count === 1 ? "singular" : "plural"
    });

    return `${count.toString()} ${name}`;
}

export function getRestrictedStatusToText(
    // oxlint-disable-next-line typescript/no-duplicate-type-constituents -- the generated aliases resolve to the same union; both names document what this accepts.
    status?: ODataRestrictedAccessControl | ODataRestrictedContentDiscovery | null
) {
    switch (status) {
        case "enabled":
            return "Enabled";
        case "disabled":
            return "Disabled";
        default:
            return "Unknown";
    }
}

export function isSearchCenterTemplate(template: string | null | undefined): boolean {
    if (template == null) {
        return false;
    }

    const normalized = template.toUpperCase();
    return normalized === "SRCHCEN" || normalized.startsWith("SRCHCEN#");
}

// External-sharing permissiveness, ranked most (3) → least (0) permissive.
const sharingCapabilityPermissiveness: Record<ODataSharingCapabilities, number> = {
    anyone: 3,
    newAndExistingGuests: 2,
    existingGuestsOnly: 1,
    noExternalSharing: 0,
    unknown: -1
};

/**
 * Maps the org-level (tenant) external-sharing enum onto the per-site
 * {@link ODataSharingCapabilities} scale so the two can be compared.
 * Returns undefined for values that don't represent a usable ceiling
 * (e.g. "unknown"/"unknownFutureValue"), meaning no restriction is applied.
 */
export function mapTenantSharingCapabilityToODataSharingCapability(
    capability: TenantSharingCapabilities
): ODataSharingCapabilities | undefined {
    switch (capability) {
        case "anyone":
            return "anyone";
        case "newAndExistingGuests":
            return "newAndExistingGuests";
        case "existingGuests":
            return "existingGuestsOnly";
        case "organizationOnly":
            return "noExternalSharing";
        default:
            return undefined;
    }
}

export function isSharingCapabilityMorePermissiveThan(
    option: ODataSharingCapabilities,
    ceiling: ODataSharingCapabilities
): boolean {
    // An unknown ceiling means the org limit couldn't be determined, so it restricts nothing.
    if (ceiling === "unknown") {
        return false;
    }

    return sharingCapabilityPermissiveness[option] > sharingCapabilityPermissiveness[ceiling];
}

type InitiatorFields =
    | Pick<ODataActivityLog, "initiatorIsAutomation" | "initiatorAutomationName" | "initiatorName">
    | Pick<ODataReview, "initiatorIsAutomation" | "initiatorAutomationName" | "initiatorName">;

export function getInitiatorDisplayName(initiator: InitiatorFields): string | null {
    if (initiator.initiatorIsAutomation) {
        return initiator.initiatorAutomationName ?? null;
    }

    return initiator.initiatorName ?? null;
}

export const ACTIVITY_LOG_DEFAULT_COLUMNS: (keyof ODataActivityLog)[] = ["status", "createdAt", "type", "summary"];

// Maps QueryableEntitySet to their corresponding OData entity type
export interface EntityTypeMap {
    users: ODataUser;
    groups: ODataGroup;
    groupPrincipals: ODataGroupPrincipal;
    siteCollections: ODataSiteCollection;
    workspaces: ODataWorkspace;
    sharedLinks: ODataSharedLink;
    sharedLinkInvitations: ODataSharedLinkInvitation;
    securityGroups: ODataSecurityGroup;
    sensitivityLabels: ODataSensitivityLabel;
    licenses: ODataLicense;
    oneDrives: ODataOneDrive;
    brokenInheritanceEntities: ODataBrokenInheritanceEntity;
    activityLogs: ODataActivityLog;
    customReports: ODataCustomReport;
    reportShares: ODataReportShare;
    userLicenses: ODataUserLicense;
    archivedSiteCollections: ODataArchivedSiteCollection;
    automations: ODataAutomation;
    reviews: ODataReview;
    reviewItems: ODataReviewItem;
    doNotDisturbUsers: ODataDoNotDisturbUser;
    doNotDisturbWorkspaces: ODataDoNotDisturbWorkspace;
    documentLibraries: ODataDocumentLibrary;
    reviewNotificationRecipients: ODataReviewNotificationRecipient;
    reviewSelectedWorkspaces: ODataReviewSelectedWorkspace;
    reviewAnswerSharedLinks: ODataReviewAnswerSharedLink;
    reviewAnswerSharedLinkRecipients: ODataReviewAnswerSharedLinkRecipient;
    reviewAnswerMembership: ODataReviewAnswerMembership;
    reviewAnswerFiles: ODataReviewAnswerFile;
    reviewReviewers: ODataReviewReviewer;
}

// Gets the OData entity type for a given QueryableEntitySet
export type EntityOf<T extends QueryableEntitySet> = EntityTypeMap[T];

// Union type of all OData entity types
export type AllEntityTypes = EntityTypeMap[keyof EntityTypeMap];

// Gets the QueryableEntitySet for a given OData entity type
export type QueryableEntitySetOf<T extends AllEntityTypes> = {
    [K in keyof EntityTypeMap]: T extends EntityTypeMap[K] ? K : never;
}[keyof EntityTypeMap];

export function getTenantName(tenant: TenantCrawlOverviewDto) {
    return tenant.displayName || tenant.tenantId;
}
